import { useEffect } from 'react'
import type { ShopWebsiteConfig } from '@/services/shopWebsiteService'

type ShopStorefrontHeadProps = {
  config: ShopWebsiteConfig
  pageTitle?: string
  pageDescription?: string
  canonicalPath?: string
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  if (!content) return
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  if (!href) return
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function upsertJsonLd(id: string, data: Record<string, unknown>) {
  let el = document.getElementById(id) as HTMLScriptElement | null
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

function absoluteUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  const origin = window.location.origin
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`
}

export function ShopStorefrontHead({
  config,
  pageTitle,
  pageDescription,
  canonicalPath,
}: ShopStorefrontHeadProps) {
  useEffect(() => {
    const siteTitle = String(config.seo_title || config.name || 'Shop').trim()
    const title = pageTitle ? `${pageTitle} | ${siteTitle}` : siteTitle
    const description = String(
      pageDescription || config.seo_description || config.description || ''
    ).trim()
    const canonical = canonicalPath ? absoluteUrl(canonicalPath) : window.location.href
    const ogImage = String(config.og_image_url || config.logo_url || '').trim()

    document.title = title
    upsertMeta('name', 'description', description)
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:type', 'website')
    upsertMeta('property', 'og:url', canonical)
    if (ogImage) upsertMeta('property', 'og:image', absoluteUrl(ogImage))
    if (config.favicon_url) upsertLink('icon', absoluteUrl(config.favicon_url))

    const org: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: config.name,
      url: canonical,
    }
    if (config.logo_url) org.logo = absoluteUrl(config.logo_url)
    upsertJsonLd('shop-org-jsonld', org)

    const website: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: siteTitle,
      url: canonical,
    }
    if (description) website.description = description
    upsertJsonLd('shop-website-jsonld', website)

    const gaId = String(config.google_analytics_id || '').trim()
    const skipGa = Boolean(config.is_preview) || config.storefront_access === 'coming_soon'
    const gaScriptId = 'shop-ga-script'
    const existingGa = document.getElementById(gaScriptId)
    if (gaId && !skipGa) {
      if (!existingGa) {
        const s = document.createElement('script')
        s.id = gaScriptId
        s.async = true
        s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`
        document.head.appendChild(s)
        const inline = document.createElement('script')
        inline.id = 'shop-ga-inline'
        inline.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId.replace(/'/g, '')}');`
        document.head.appendChild(inline)
      }
    } else {
      existingGa?.remove()
      document.getElementById('shop-ga-inline')?.remove()
    }
  }, [config, pageTitle, pageDescription, canonicalPath])

  return null
}
